/* prettier-ignore-file */
import React, { useState, useEffect } from "react";
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  Switch,
} from "react-native";
import { useNavigate } from "react-router-dom";
import { getFunctions, httpsCallable } from "firebase/functions";
import { app } from "../firebase.config";
import AuthHeader from "../components/AuthHeader";
import { useUsersStore, useStripeTestMode } from "../stores";

const functions = getFunctions(app);

const Admin = () => {
  const navigate = useNavigate();
  const { currentUser } = useUsersStore();
  const useTestStripe = useStripeTestMode((state) => state.useTestStripe);
  const setUseTestStripe = useStripeTestMode((state) => state.setUseTestStripe);
  
  // User Control is enabled for all users on the admin page
  const canAccessUserSettings = true;

  // Desktop detection
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 768);
  const [togglingTestMode, setTogglingTestMode] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth >= 768);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    console.log("Admin page: auth user details", currentUser ?? "no user");
  }, [currentUser]);

  const handleToggleTestMode = (newValue) => {
    setTogglingTestMode(true);
    setUseTestStripe(newValue);
    setTogglingTestMode(false);
  };

  // POS sync — same job the 5:30 AM schedule runs, triggered on demand.
  const [posSyncing, setPosSyncing] = useState(false);
  const [posSyncResult, setPosSyncResult] = useState(null);

  const handleSyncRentalsToPos = async () => {
    setPosSyncing(true);
    setPosSyncResult(null);
    try {
      const syncNow = httpsCallable(functions, "runCadenceRentalSyncNow");
      const { data } = await syncNow();
      setPosSyncResult({
        ok: !data.errors?.length,
        text: `Sent ${data.eventsSent} event(s) across ${data.storesPaired} paired store(s).`,
        errors: data.errors || [],
      });
    } catch (error) {
      console.error("❌ POS sync failed:", error);
      setPosSyncResult({ ok: false, text: `Sync failed: ${error.message}`, errors: [] });
    } finally {
      setPosSyncing(false);
    }
  };

  return (
    <View style={styles.container}>
      <AuthHeader
        title="Admin"
        subtitle="Administrative Controls"
        isAuthenticated={true}
        isDesktop={isDesktop}
      />
      
      <View style={isDesktop ? styles.desktopWrapper : null}>
        <ScrollView style={styles.content}>
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Administrative Tools</Text>
          
          {/* User Settings Link - Only enabled if authLevel >= 4 */}
          <TouchableOpacity dataSet={{ hover: "lift" }}
            style={[
              styles.linkButton,
              !canAccessUserSettings && styles.linkButtonDisabled
            ]}
            onPress={() => {
              if (canAccessUserSettings) {
                navigate("/user-settings");
              }
            }}
            disabled={!canAccessUserSettings}
          >
            <Text style={styles.linkIcon}>👥</Text>
            <Text style={[
              styles.linkText,
              !canAccessUserSettings && styles.linkTextDisabled
            ]}>
              User Control
            </Text>
          </TouchableOpacity>

          {/* Settings Link */}
          <TouchableOpacity dataSet={{ hover: "lift" }}
            style={styles.linkButton}
            onPress={() => navigate("/settings")}
          >
            <Text style={styles.linkIcon}>⚙️</Text>
            <Text style={styles.linkText}>Settings</Text>
          </TouchableOpacity>
        </View>

        {/* POS Sync Section */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>POS Sync</Text>
          <Text style={styles.sectionDescription}>
            Sends today's rental starts and returns to the POS. This runs on its own
            every morning at 5:30 AM — use the button to run it again now.
          </Text>

          <TouchableOpacity dataSet={{ hover: "lift" }}
            style={[styles.linkButton, posSyncing && styles.linkButtonDisabled]}
            onPress={handleSyncRentalsToPos}
            disabled={posSyncing}
          >
            <Text style={styles.linkIcon}>{posSyncing ? "⏳" : "🚲"}</Text>
            <Text style={styles.linkText}>
              {posSyncing ? "Syncing…" : "Send Today's Rentals to POS"}
            </Text>
          </TouchableOpacity>

          {posSyncResult && (
            <View style={posSyncResult.ok ? styles.posResultOk : styles.posResultError}>
              <Text style={styles.posResultText}>{posSyncResult.text}</Text>
              {posSyncResult.errors.map((err, i) => (
                <Text key={i} style={styles.posResultErrorText}>{err}</Text>
              ))}
            </View>
          )}
        </View>

        {/* Stripe Test Mode Section */}
        <View style={[styles.section, useTestStripe && styles.testModeSection]}>
          <Text style={styles.sectionTitle}>Stripe Test Mode</Text>
          <View style={styles.testModeRow}>
            <View style={{ flex: 1 }}>
              <Text style={styles.linkText}>
                {useTestStripe ? "Test Mode is ON" : "Test Mode is OFF"}
              </Text>
              <Text style={styles.testModeDescription}>
                {useTestStripe
                  ? "All payments use the Stripe sandbox. No real charges."
                  : "Payments are live. Real cards will be charged."}
              </Text>
            </View>
            <Switch
              value={useTestStripe}
              onValueChange={handleToggleTestMode}
              disabled={togglingTestMode}
              trackColor={{ false: "#DDE3E2", true: "#C4453D" }}
              thumbColor={useTestStripe ? "#FFFFFF" : "#FFFFFF"}
            />
          </View>
          {useTestStripe && (
            <View style={styles.testModeWarningBanner}>
              <Text style={styles.testModeWarningText}>
                STRIPE TEST MODE ACTIVE — Use card 4242 4242 4242 4242
              </Text>
            </View>
          )}
        </View>

      </ScrollView>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#F6F8F7",
    minHeight: "100vh",
    display: "flex",
    flexDirection: "column",
  },
  desktopWrapper: {
    width: "100%",
    maxWidth: 800,
    flex: 1,
    alignSelf: "center",
  },
  content: {
    flex: 1,
    padding: 16,
  },
  section: {
    backgroundColor: "#FFFFFF",
    borderRadius: 8,
    padding: 16,
    marginBottom: 16,
    boxShadow: "0 1px 2px rgba(15, 26, 25, 0.06), 0 2px 8px rgba(15, 26, 25, 0.06)",
    elevation: 3,
  },
  sectionTitle: {
    fontSize: 20,
    fontWeight: "bold",
    color: "#2E3A38",
    marginBottom: 16,
  },
  linkButton: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 16,
    paddingHorizontal: 16,
    backgroundColor: "#F4F6F6",
    borderRadius: 6,
    marginBottom: 12,
    borderWidth: 1,
    borderColor: "#DDE3E2",
  },
  linkButtonDisabled: {
    opacity: 0.5,
    backgroundColor: "#EDF1F0",
  },
  linkIcon: {
    fontSize: 24,
    marginRight: 12,
  },
  linkTextContainer: {
    flex: 1,
  },
  linkText: {
    fontSize: 16,
    fontWeight: "500",
    color: "#2E3A38",
  },
  linkTextDisabled: {
    color: "#8A9794",
  },
  linkSubtext: {
    fontSize: 12,
    color: "#8A9794",
    marginTop: 4,
  },
  sectionDescription: {
    fontSize: 13,
    color: "#5C6B68",
    marginBottom: 12,
  },
  posResultOk: {
    backgroundColor: "#EEF5F2",
    borderRadius: 6,
    padding: 12,
    borderWidth: 1,
    borderColor: "#BBD6CB",
  },
  posResultError: {
    backgroundColor: "#FBEEED",
    borderRadius: 6,
    padding: 12,
    borderWidth: 1,
    borderColor: "#E3B4B0",
  },
  posResultText: {
    fontSize: 13,
    color: "#2E3A38",
  },
  posResultErrorText: {
    fontSize: 13,
    color: "#C4453D",
    marginTop: 4,
  },
  testModeSection: {
    borderWidth: 2,
    borderColor: "#C4453D",
  },
  testModeRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 12,
  },
  testModeDescription: {
    fontSize: 13,
    color: "#5C6B68",
    marginTop: 4,
  },
  testModeWarningBanner: {
    backgroundColor: "#C4453D",
    borderRadius: 6,
    padding: 12,
    alignItems: "center",
  },
  testModeWarningText: {
    color: "#FFFFFF",
    fontSize: 13,
    fontWeight: "bold",
    textAlign: "center",
  },
});

export default Admin;

